import { useEffect, useMemo, useState, useRef } from "react";
import { Center, Flex, Loader, Title, Badge, useMantineTheme } from "@mantine/core";
import {
  MantineReactTable,
  type MRT_ColumnDef,
  type MRT_SortingState,
  type MRT_ColumnFiltersState,
} from "mantine-react-table";
import trpc from "@/shared/utils/trpc";
import { handleTrpcError } from "@/shared/utils/trpc/handletrpcError.util";
import { useDebouncedValue } from "@mantine/hooks";

type InterestListItem = {
  id: string;
  requester: {
    id: string;
    name: string | null;
  };
  receiver: {
    id: string;
    name: string | null;
  };
  request_status: string;
  payment_status: string;
  createdAt: Date;
};

const REQUEST_STATUS_LABELS: Record<string, string> = {
  REQUESTED: "Sent",
  RECEIVER_DECLINED: "Declined",
  BOTH_ACCEPTED: "Accepted",
  REQUESTER_PAID_ONLY: "Accepted",
  RECEIVER_PAID_ONLY: "Accepted",
  PAYMENT_EXPIRED: "Expired",
};

const REQUEST_STATUS_COLORS: Record<string, string> = {
  REQUESTED: "blue",
  RECEIVER_DECLINED: "red",
  BOTH_ACCEPTED: "cyan",
  REQUESTER_PAID_ONLY: "cyan",
  RECEIVER_PAID_ONLY: "cyan",
  PAYMENT_EXPIRED: "gray",
};

const PAYMENT_STATUS_LABELS: Record<string, string> = {
  BOTH_ACCEPTED: "Awaiting Payment",
  REQUESTER_PAID_ONLY: "Requester Paid",
  RECEIVER_PAID_ONLY: "Requestee Paid",
  PAYMENT_EXPIRED: "Payment Expired",
};

const PAYMENT_STATUS_COLORS: Record<string, string> = {
  BOTH_ACCEPTED: "yellow",
  REQUESTER_PAID_ONLY: "orange",
  RECEIVER_PAID_ONLY: "orange",
  PAYMENT_EXPIRED: "gray",
};

const PAGE_SIZE = 10;

export default function AdminInterestManagementPage() {
  const theme = useMantineTheme();

  const [interests, setInterests] = useState<InterestListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalRows, setTotalRows] = useState(0);

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: PAGE_SIZE,
  });
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>([]);
  const [sorting, setSorting] = useState<MRT_SortingState>([]);
  const [debouncedFilters] = useDebouncedValue(columnFilters, 400);

  const previousParamsRef = useRef<string>("");

  const filterParams = useMemo(() => {
    const filtered = debouncedFilters.filter((f) => f.value !== undefined && f.value !== null && f.value !== "");
    return Object.fromEntries(filtered.map((f) => [f.id, f.value]));
  }, [debouncedFilters]);

  useEffect(() => {
    const params = {
      page: pagination.pageIndex + 1,
      limit: pagination.pageSize,
      sorting: sorting.map((s) => ({ id: s.id, desc: s.desc })),
    };

    const paramsString = JSON.stringify({ ...params, filterParams });

    if (paramsString === previousParamsRef.current) {
      return;
    }

    previousParamsRef.current = paramsString;

    const fetchInterests = async () => {
      setLoading(true);
      try {
        const filter: Record<string, any> = {};

        if (filterParams.search) {
          filter.search = filterParams.search;
        }
        if (filterParams.request_status) {
          filter.status = filterParams.request_status;
        }
        if (filterParams.payment_status) {
          filter.paymentStatus = filterParams.payment_status;
        }

        const res = await trpc.admin.getAllInterests.query({
          page: params.page,
          limit: params.limit,
          filter: Object.keys(filter).length > 0 ? filter : undefined,
          sorting: params.sorting.length > 0 ? params.sorting : undefined,
        });

        setInterests(
          res.interests.map((item: any) => ({
            id: item.id,
            requester: item.requester,
            receiver: item.receiver,
            request_status: item.request_status,
            payment_status: item.payment_status,
            createdAt: new Date(item.createdAt),
          }))
        );
        setTotalRows(res.meta.total);
      } catch (err) {
        handleTrpcError(err);
        setInterests([]);
        setTotalRows(0);
      } finally {
        setLoading(false);
      }
    };

    fetchInterests();
  }, [pagination.pageIndex, pagination.pageSize, filterParams, sorting]);

  useEffect(() => {
    if (pagination.pageIndex !== 0) {
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    }
  }, [filterParams]);

  const columns: MRT_ColumnDef<InterestListItem>[] = useMemo(
    () => [
      {
        accessorKey: "requester",
        header: "Requester",
        size: 200,
        Cell: ({ cell }) => {
          const value = cell.getValue() as InterestListItem["requester"];
          return value?.name || "—";
        },
      },
      {
        accessorKey: "receiver",
        header: "Requestee",
        size: 200,
        Cell: ({ cell }) => {
          const value = cell.getValue() as InterestListItem["receiver"];
          return value?.name || "—";
        },
      },
      {
        accessorKey: "request_status",
        header: "Request Status",
        size: 160,
        filterVariant: "select",
        mantineFilterSelectProps: {
          data: [
            { value: "REQUESTED", label: "Sent" },
            { value: "RECEIVER_DECLINED", label: "Declined" },
            { value: "BOTH_ACCEPTED", label: "Accepted" },
          ],
          clearable: true,
        },
        Cell: ({ cell }) => {
          const value = cell.getValue() as string;
          const color = REQUEST_STATUS_COLORS[value] || "gray";
          const label = REQUEST_STATUS_LABELS[value] || value;
          return (
            <Badge variant="light" color={color} size="md" fz="xs" tt="none">
              {label}
            </Badge>
          );
        },
      },
      {
        accessorKey: "payment_status",
        header: "Payment Status",
        size: 160,
        filterVariant: "select",
        mantineFilterSelectProps: {
          data: [
            { value: "REQUESTER_PAID_ONLY", label: "Requester Paid" },
            { value: "RECEIVER_PAID_ONLY", label: "Requestee Paid" },
            { value: "PAYMENT_EXPIRED", label: "Expired" },
          ],
          clearable: true,
        },
        Cell: ({ row }) => {
          const requestStatus = row.original.request_status;

          // Only show payment status for statuses after BOTH_ACCEPTED
          if (requestStatus === "REQUESTED" || requestStatus === "RECEIVER_DECLINED") {
            return <span style={{ color: "#94a3b8" }}>—</span>;
          }

          const color = PAYMENT_STATUS_COLORS[requestStatus] || "gray";
          const label = PAYMENT_STATUS_LABELS[requestStatus] || "—";

          return (
            <Badge variant="light" color={color} size="md" fz="xs" tt="none">
              {label}
            </Badge>
          );
        },
      },
      {
        accessorKey: "createdAt",
        header: "Created At",
        size: 180,
        enableColumnFilter: false,
        Cell: ({ cell }) => {
          const value = cell.getValue() as Date;
          if (!value) return <span style={{ color: "#94a3b8" }}>—</span>;
          return value.toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          });
        },
      },
    ],
    []
  );

  if (loading && interests.length === 0) {
    return (
      <Center h="50vh">
        <Loader size="lg" variant="dots" />
      </Center>
    );
  }

  return (
    <Flex direction="column" w="100%" p="xl">
      <Title order={2} fw={400} mb="xl">
        Interest Management
      </Title>

      <MantineReactTable
        columns={columns}
        data={interests}
        enableRowNumbers
        enableStickyHeader
        enableColumnPinning
        enableColumnResizing
        columnResizeMode="onEnd"
        layoutMode="semantic"
        manualPagination
        manualFiltering
        manualSorting
        enableGlobalFilter={false}
        enableRowActions={false}
        rowCount={totalRows}
        onPaginationChange={setPagination}
        onColumnFiltersChange={setColumnFilters}
        onSortingChange={setSorting}
        state={{
          pagination,
          columnFilters,
          sorting,
          isLoading: loading,
        }}
        initialState={{
          density: "md",
        }}
        mantineTableBodyCellProps={{
          style: {
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            maxWidth: 200,
          },
        }}
        mantineTableHeadCellProps={{
          style: {
            fontWeight: 500,
            fontSize: "var(--mantine-font-size-md)",
            color: theme.colors.slate[6],
          },
        }}
        mantineTableContainerProps={{
          style: {
            maxHeight: "calc(100vh - 250px)",
            minHeight: "100px",
            width: "100%",
            overflowX: "auto",
          },
        }}
        renderEmptyRowsFallback={() => (
          <tr>
            <td colSpan={columns.length + 1} style={{ textAlign: "center", padding: "2rem" }}>
              No interest requests found. Try adjusting your filters.
            </td>
          </tr>
        )}
      />
    </Flex>
  );
}
